import { SkillPickerCategoryTabs } from "@/components/Assist/skill/SkillPickerCategoryTabs";
import type { SkillPickerTab } from "@/components/Assist/skill/skills-picker-lib";
import {
  GroupedTable,
  GroupedTableColumn,
} from "@/components/GroupedTable/GroupedTable";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { KitDeletionDialog } from "@/components/dependency-deletion/KitDeletionDialog";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import { SKILL_CATEGORIES } from "@/components/skill/constants";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Column, Row } from "@/components/ui/flex";
import { Input } from "@/components/ui/input";
import { useGroupExpertsData } from "@/data/use-group-experts-data";
import { useGroupFoldersData } from "@/data/use-group-folder-data";
import { useGroupSkillsData } from "@/data/use-group-skills-data";
import { useKitsData } from "@/data/use-kits-data";
import { useGroupTemplatesData } from "@/data/use-templates-data";
import { useUsersData } from "@/data/use-users-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { groupByDate, smartDate } from "@/lib/date";
import {
  GetKitDto,
  groupKitExpertLinkControllerLink,
  groupKitFolderLinkControllerLink,
  groupKitSkillLinkControllerLink,
  groupKitTemplateLinkControllerLink,
} from "@/lib/gen-api";
import { getInitials } from "@/lib/string";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useMemo, useState } from "react";
import { toast } from "sonner";

const ALL_TAB: SkillPickerTab = "all";
const UNCATEGORIZED_TAB: SkillPickerTab = "uncategorized";
const UNCATEGORIZED_LABEL = "Uncategorized";

const CATEGORY_SET = new Set<string>(SKILL_CATEGORIES);

function normalizeSkillCategory(category: string | null): SkillPickerTab {
  if (!category || !CATEGORY_SET.has(category)) {
    return UNCATEGORIZED_TAB;
  }
  return category as SkillPickerTab;
}

type ArtifactTab = "skills" | "experts" | "folders" | "templates";

const ARTIFACT_TABS: { key: ArtifactTab; label: string }[] = [
  { key: "skills", label: "Skills" },
  { key: "experts", label: "Experts" },
  { key: "folders", label: "Folders" },
  { key: "templates", label: "Templates" },
];

function toggleUuid(current: string[], uuid: string): string[] {
  return current.includes(uuid)
    ? current.filter((u) => u !== uuid)
    : [...current, uuid];
}

/** Owned-only, so a Collection only ever bundles what this group has direct control over. */
export default function CollectionsPage() {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const [activeArtifactTab, setActiveArtifactTab] =
    useState<ArtifactTab>("skills");
  const [isCreating, setIsCreating] = useState(false);

  const [selectedSkillUuids, setSelectedSkillUuids] = useState<string[]>([]);
  const [skillSearch, setSkillSearch] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<SkillPickerTab>(ALL_TAB);

  const [selectedExpertUuids, setSelectedExpertUuids] = useState<string[]>([]);
  const [expertSearch, setExpertSearch] = useState("");

  const [selectedFolderUuids, setSelectedFolderUuids] = useState<string[]>([]);
  const [folderSearch, setFolderSearch] = useState("");

  const [selectedTemplateUuids, setSelectedTemplateUuids] = useState<string[]>(
    [],
  );
  const [templateSearch, setTemplateSearch] = useState("");

  const {
    kits: collections,
    createKit,
    isCreatingKit,
  } = useKitsData(groupUuid, {
    type: "collection",
    owned: true,
  });

  const { skills: ownedSkills, isLoading: isLoadingSkills } =
    useGroupSkillsData({
      groupUuid,
      access: "owner",
    });
  const { experts: ownedExperts, isLoading: isLoadingExperts } =
    useGroupExpertsData(groupUuid, { access: "owner" });
  const { folders: ownedFolders, isLoading: isLoadingFolders } =
    useGroupFoldersData({
      groupUuid,
      access: "owner",
    });
  const { templates: ownedTemplates, isLoading: isLoadingTemplates } =
    useGroupTemplatesData({
      groupUuid,
      access: "owner",
    });

  const categoryTabs = useMemo(() => {
    const present = new Set(
      ownedSkills.map((skill) => normalizeSkillCategory(skill.category)),
    );

    const tabs: { key: SkillPickerTab; label: string }[] = [
      { key: ALL_TAB, label: "All" },
    ];
    for (const category of SKILL_CATEGORIES) {
      if (present.has(category)) {
        tabs.push({ key: category, label: category });
      }
    }
    if (present.has(UNCATEGORIZED_TAB)) {
      tabs.push({ key: UNCATEGORIZED_TAB, label: UNCATEGORIZED_LABEL });
    }
    return tabs;
  }, [ownedSkills]);

  const filteredSkills = useMemo(
    () =>
      ownedSkills.filter((skill) => {
        const matchesSearch = skill.name
          .toLowerCase()
          .includes(skillSearch.toLowerCase());
        const matchesCategory =
          selectedCategory === ALL_TAB ||
          normalizeSkillCategory(skill.category) === selectedCategory;
        return matchesSearch && matchesCategory;
      }),
    [ownedSkills, skillSearch, selectedCategory],
  );

  const filteredExperts = useMemo(
    () =>
      ownedExperts.filter((expert) =>
        expert.name.toLowerCase().includes(expertSearch.toLowerCase()),
      ),
    [ownedExperts, expertSearch],
  );

  const filteredFolders = useMemo(
    () =>
      ownedFolders.filter((folder) =>
        folder.name.toLowerCase().includes(folderSearch.toLowerCase()),
      ),
    [ownedFolders, folderSearch],
  );

  const filteredTemplates = useMemo(
    () =>
      ownedTemplates.filter((template) =>
        template.name.toLowerCase().includes(templateSearch.toLowerCase()),
      ),
    [ownedTemplates, templateSearch],
  );

  const groupedCollections = groupByDate(collections, "date_updated");

  const columns: GroupedTableColumn<GetKitDto>[] = [
    { key: "name", label: "Name" },
    { key: "date_updated", label: "Last Modified" },
    { key: "user_uuid", label: "Created By" },
  ];

  function resetDialog() {
    setIsDialogOpen(false);
    setCollectionName("");
    setActiveArtifactTab("skills");
    setSelectedSkillUuids([]);
    setSkillSearch("");
    setSelectedCategory(ALL_TAB);
    setSelectedExpertUuids([]);
    setExpertSearch("");
    setSelectedFolderUuids([]);
    setFolderSearch("");
    setSelectedTemplateUuids([]);
    setTemplateSearch("");
  }

  async function handleCreateCollection() {
    const name = collectionName.trim();
    if (!name || !groupUuid) {
      return;
    }

    setIsCreating(true);
    try {
      const kit = await createKit({ name });
      if (!kit) {
        toast.error("Failed to create collection. Please try again.");
        return;
      }

      await Promise.all([
        ...selectedSkillUuids.map((skillUuid) =>
          groupKitSkillLinkControllerLink({
            path: { groupUuid, kitUuid: kit.uuid, skillUuid },
          }),
        ),
        ...selectedExpertUuids.map((expertUuid) =>
          groupKitExpertLinkControllerLink({
            path: { groupUuid, kitUuid: kit.uuid, expertUuid },
          }),
        ),
        ...selectedFolderUuids.map((folderUuid) =>
          groupKitFolderLinkControllerLink({
            path: { groupUuid, kitUuid: kit.uuid, folderUuid },
          }),
        ),
        ...selectedTemplateUuids.map((templateUuid) =>
          groupKitTemplateLinkControllerLink({
            path: { groupUuid, kitUuid: kit.uuid, templateUuid },
          }),
        ),
      ]);

      toast.success("Collection created successfully!");
      resetDialog();
    } catch {
      toast.error("Failed to create collection. Please try again.");
    } finally {
      setIsCreating(false);
    }
  }

  const selectedCounts: Record<ArtifactTab, number> = {
    skills: selectedSkillUuids.length,
    experts: selectedExpertUuids.length,
    folders: selectedFolderUuids.length,
    templates: selectedTemplateUuids.length,
  };

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Collections</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <Column>
          <p>
            Collections let you group{" "}
            <span className="text-accent">Skills</span>,{" "}
            <span className="text-accent">Experts</span>,{" "}
            <span className="text-accent">Knowledge Folders</span>, and{" "}
            <span className="text-accent">Document Templates</span> owned in
            this workspace into a reusable bundle. Share a Collection with
            another workspace to make everything in it usable there at once.
          </p>
        </Column>
        <Row>
          <Dialog
            open={isDialogOpen}
            onOpenChange={(open) =>
              open ? setIsDialogOpen(true) : resetDialog()
            }
          >
            <DialogTrigger asChild>
              <Button>Create Collection</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[680px]">
              <DialogHeader>
                <DialogTitle>Create New Collection</DialogTitle>
                <DialogDescription>
                  Name your Collection and choose the artifacts you own to
                  include in it.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-4 py-4">
                <Input
                  value={collectionName}
                  onChange={(e) => setCollectionName(e.target.value)}
                  placeholder="Name"
                  autoFocus
                />
                <div className="flex gap-4">
                  <div className="flex w-36 shrink-0 flex-col gap-1">
                    {ARTIFACT_TABS.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveArtifactTab(tab.key)}
                        className={cn(
                          "flex cursor-pointer items-center justify-between gap-2 rounded-sm px-2.5 py-1.5 text-left text-sm transition-colors",
                          activeArtifactTab === tab.key
                            ? "bg-accent-10 text-accent-foreground font-bold"
                            : "hover:bg-accent-3 text-foreground dark:text-muted-foreground",
                        )}
                      >
                        <span>{tab.label}</span>
                        {selectedCounts[tab.key] > 0 && (
                          <Badge variant="secondary">
                            {selectedCounts[tab.key]}
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="min-w-0 flex-1">
                    {activeArtifactTab === "skills" && (
                      <div className="flex flex-col gap-4">
                        <Input
                          value={skillSearch}
                          onChange={(e) => setSkillSearch(e.target.value)}
                          placeholder="Search your Skills"
                        />
                        {categoryTabs.length > 1 && (
                          <SkillPickerCategoryTabs
                            tabs={categoryTabs}
                            selectedTab={selectedCategory}
                            onSelectTab={setSelectedCategory}
                          />
                        )}
                        <ArtifactCheckboxList
                          items={filteredSkills}
                          hasOwnedItems={ownedSkills.length > 0}
                          selectedUuids={selectedSkillUuids}
                          onToggle={(uuid) =>
                            setSelectedSkillUuids((current) =>
                              toggleUuid(current, uuid),
                            )
                          }
                          isLoading={isLoadingSkills}
                          emptyMessage="You don't own any Skills in this group yet."
                          noMatchMessage="No Skills match your search."
                        />
                      </div>
                    )}

                    {activeArtifactTab === "experts" && (
                      <div className="flex flex-col gap-2">
                        <Input
                          value={expertSearch}
                          onChange={(e) => setExpertSearch(e.target.value)}
                          placeholder="Search your Experts"
                        />
                        <ArtifactCheckboxList
                          items={filteredExperts}
                          hasOwnedItems={ownedExperts.length > 0}
                          selectedUuids={selectedExpertUuids}
                          onToggle={(uuid) =>
                            setSelectedExpertUuids((current) =>
                              toggleUuid(current, uuid),
                            )
                          }
                          isLoading={isLoadingExperts}
                          emptyMessage="You don't own any Experts in this group yet."
                          noMatchMessage="No Experts match your search."
                        />
                      </div>
                    )}

                    {activeArtifactTab === "folders" && (
                      <div className="flex flex-col gap-2">
                        <Input
                          value={folderSearch}
                          onChange={(e) => setFolderSearch(e.target.value)}
                          placeholder="Search your Knowledge Folders"
                        />
                        <ArtifactCheckboxList
                          items={filteredFolders}
                          hasOwnedItems={ownedFolders.length > 0}
                          selectedUuids={selectedFolderUuids}
                          onToggle={(uuid) =>
                            setSelectedFolderUuids((current) =>
                              toggleUuid(current, uuid),
                            )
                          }
                          isLoading={isLoadingFolders}
                          emptyMessage="You don't own any Knowledge Folders in this group yet."
                          noMatchMessage="No Knowledge Folders match your search."
                        />
                      </div>
                    )}

                    {activeArtifactTab === "templates" && (
                      <div className="flex flex-col gap-2">
                        <Input
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                          placeholder="Search your Document Templates"
                        />
                        <ArtifactCheckboxList
                          items={filteredTemplates}
                          hasOwnedItems={ownedTemplates.length > 0}
                          selectedUuids={selectedTemplateUuids}
                          onToggle={(uuid) =>
                            setSelectedTemplateUuids((current) =>
                              toggleUuid(current, uuid),
                            )
                          }
                          isLoading={isLoadingTemplates}
                          emptyMessage="You don't own any Document Templates in this group yet."
                          noMatchMessage="No Document Templates match your search."
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetDialog}
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  onClick={handleCreateCollection}
                  disabled={
                    !collectionName.trim() || isCreating || isCreatingKit
                  }
                  size="sm"
                >
                  Create Collection
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Row>
        <GroupedTable
          data={groupedCollections}
          columns={columns}
          itemIdKey="uuid"
          renderCell={{
            name: (_, row) => <KitDisplayName kit={row} />,
            user_uuid: (value) => <UserCell userUuid={value} />,
            date_updated: (value) => smartDate(value),
          }}
          renderActions={({ row }) => <CollectionRowActions row={row} />}
          rowLink={(row) => ({
            to: "/w/$groupSlug/k/$kitUuid/collection-settings",
            params: { groupSlug, kitUuid: row.uuid },
          })}
          emptyMessage="No Collections here yet. Create your first one to bundle Skills, Experts, Knowledge Folders, and Document Templates together for sharing."
        />
      </PageBody>
    </Page>
  );
}

function ArtifactCheckboxList({
  items,
  hasOwnedItems,
  selectedUuids,
  onToggle,
  isLoading,
  emptyMessage,
  noMatchMessage,
}: {
  items: { uuid: string; name: string }[];
  hasOwnedItems: boolean;
  selectedUuids: string[];
  onToggle: (uuid: string) => void;
  isLoading: boolean;
  emptyMessage: string;
  noMatchMessage: string;
}) {
  return (
    <div className="flex max-h-[260px] flex-col gap-1 overflow-y-auto">
      {isLoading ? (
        <p className="text-foreground-5 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-foreground-5 text-sm">
          {hasOwnedItems ? noMatchMessage : emptyMessage}
        </p>
      ) : (
        items.map((item) => (
          <label
            key={item.uuid}
            className="hover:bg-background-3 flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5"
          >
            <Checkbox
              checked={selectedUuids.includes(item.uuid)}
              onCheckedChange={() => onToggle(item.uuid)}
            />
            <span className="truncate text-sm">{item.name}</span>
          </label>
        ))
      )}
    </div>
  );
}

function UserCell({ userUuid }: { userUuid: string }) {
  const groupUuid = useGroupUuid();
  const { getUserForUserUuid } = useUsersData(groupUuid);
  return (
    <Row className="items-center gap-2">
      <Avatar className="h-5 w-5">
        <AvatarFallback>
          {getInitials(getUserForUserUuid(userUuid)?.name || "", 2)}
        </AvatarFallback>
        <AvatarImage
          src={getUserForUserUuid(userUuid)?.photo_url ?? undefined}
        />
      </Avatar>
      <span>{getUserForUserUuid(userUuid)?.name}</span>
    </Row>
  );
}

function CollectionRowActions({ row }: { row: GetKitDto }) {
  return (
    <Row className="items-center gap-2">
      <KitDeletionDialog kitUuid={row.uuid} groupUuid={row.group_uuid}>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-destructive hover:text-destructive-foreground"
          aria-label="Delete"
          title="Delete"
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </KitDeletionDialog>
    </Row>
  );
}
